import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {
  PayrollCategoryState,
  FetchPayrollCategoriesResponse,
  FetchPayrollCategoryByIdResponse,
  FetchAllPayrollCategoriesResponse,
} from './types'
import {
  fetchPayrollCategoryById,
  fetchPayrollCategories,
  fetchAllPayrollCategories,
  createPayrollCategory,
  updatePayrollCategory,
  deletePayrollCategory,
  suspendPayrollCategory,
  activatePayrollCategory,
} from './service'

const initialState: PayrollCategoryState = {
  payrollCategoryForEdit: null,
  payrollCategories: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  actionLoading: false,
  error: null,
}

const payrollCategorySlice = createSlice({
  name: 'payrollCategory',
  initialState,
  reducers: {
    resetPayrollCategories: (state: PayrollCategoryState) => {
      state.payrollCategories = []
      state.total = 0
      state.totalPages = 0
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPayrollCategories.pending, (state: PayrollCategoryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchPayrollCategories.rejected, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.listLoading = false
    })
    builder.addCase(
      fetchPayrollCategories.fulfilled,
      (state: PayrollCategoryState, action: PayloadAction<FetchPayrollCategoriesResponse>) => {
        const {payrollCategories, total, totalPages} = action.payload
        state.payrollCategories = payrollCategories
        state.total = total
        state.totalPages = totalPages
        state.listLoading = false
      }
    )

    builder.addCase(fetchAllPayrollCategories.pending, (state: PayrollCategoryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchAllPayrollCategories.rejected, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.listLoading = false
    })
    builder.addCase(
      fetchAllPayrollCategories.fulfilled,
      (state: PayrollCategoryState, action: PayloadAction<FetchAllPayrollCategoriesResponse>) => {
        const {payrollCategories} = action.payload
        state.payrollCategories = payrollCategories
        state.listLoading = false
      }
    )

    builder.addCase(fetchPayrollCategoryById.pending, (state: PayrollCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(fetchPayrollCategoryById.rejected, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      fetchPayrollCategoryById.fulfilled,
      (state: PayrollCategoryState, action: PayloadAction<FetchPayrollCategoryByIdResponse>) => {
        const {payrollCategory} = action.payload
        state.payrollCategoryForEdit = payrollCategory
        state.actionLoading = false
      }
    )

    builder.addCase(createPayrollCategory.pending, (state: PayrollCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(createPayrollCategory.rejected, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(createPayrollCategory.fulfilled, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {payrollCategory} = action.payload
      state.payrollCategories = [payrollCategory, ...state.payrollCategories]
      state.total = state.total + 1
      state.actionLoading = false
    })

    builder.addCase(updatePayrollCategory.pending, (state: PayrollCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(updatePayrollCategory.rejected, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(updatePayrollCategory.fulfilled, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {payrollCategory} = action.payload
      state.payrollCategories = state.payrollCategories.map((p) => (p._id === payrollCategory._id ? payrollCategory : p))
      if (state.payrollCategoryForEdit?._id === payrollCategory._id) {
        state.payrollCategoryForEdit = payrollCategory
      }
      state.actionLoading = false
    })

    builder.addCase(deletePayrollCategory.pending, (state: PayrollCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(deletePayrollCategory.rejected, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(deletePayrollCategory.fulfilled, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {payrollCategory} = action.payload
      state.payrollCategories = state.payrollCategories.filter((p) => p._id !== payrollCategory._id)
      state.total = state.total - 1
      if (state.payrollCategoryForEdit?._id === payrollCategory._id) {
        state.payrollCategoryForEdit = null
      }
      state.actionLoading = false
    })

    builder.addCase(suspendPayrollCategory.pending, (state: PayrollCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(suspendPayrollCategory.rejected, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(suspendPayrollCategory.fulfilled, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {payrollCategory} = action.payload
      state.payrollCategories = state.payrollCategories.map((p) => (p._id === payrollCategory._id ? payrollCategory : p))
      if (state.payrollCategoryForEdit?._id === payrollCategory._id) {
        state.payrollCategoryForEdit = payrollCategory
      }
      state.actionLoading = false
    })

    builder.addCase(activatePayrollCategory.pending, (state: PayrollCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(activatePayrollCategory.rejected, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(activatePayrollCategory.fulfilled, (state: PayrollCategoryState, action: PayloadAction<any>) => {
      const {payrollCategory} = action.payload
      state.payrollCategories = state.payrollCategories.map((p) => (p._id === payrollCategory._id ? payrollCategory : p))
      if (state.payrollCategoryForEdit?._id === payrollCategory._id) {
        state.payrollCategoryForEdit = payrollCategory
      }
      state.actionLoading = false
    })
  },
})

export const {resetPayrollCategories} = payrollCategorySlice.actions
export default payrollCategorySlice.reducer

